// src/SalesWindow.js
import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "./api";

export default function SalesWindow({ onBack }) {
  /* -------------------- session context -------------------- */
  const userStr = localStorage.getItem("user") || "{}";
  let userObj = {};
  try {
    userObj = JSON.parse(userStr);
  } catch {
    userObj = {};
  }

  // Supports both shapes: {username} OR {user:{username}}
  const username = (
    userObj?.user?.username ||
    userObj?.username ||
    localStorage.getItem("username") ||
    "cashier"
  ).trim();

  const storeId = Number(localStorage.getItem("store_id") || 0);

  // stable cart key per store+user
  const SESSION_ID = useMemo(() => `store${storeId}_${username}`, [storeId, username]);

  /* -------------------- state -------------------- */
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [cart, setCart] = useState([]);
  const [paid, setPaid] = useState("");

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Quantity modal
  const [qtyBoxOpen, setQtyBoxOpen] = useState(false);
  const [qtyItem, setQtyItem] = useState(null);
  const [qtyInput, setQtyInput] = useState("1");
  const [qtyError, setQtyError] = useState("");

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 3000);
  };

  /* -------------------- headers -------------------- */
  useEffect(() => {
    if (storeId) api.defaults.headers.common["X-Store-ID"] = String(storeId);
    if (username) api.defaults.headers.common["X-User"] = username;
  }, [storeId, username]);

  /* -------------------- load inventory -------------------- */
  const loadInventory = async () => {
    try {
      const res = await api.get("/server.php/inventory");

      // Accept server shapes:
      // 1) [ ... ]
      // 2) { success:true, rows:[ ... ] }
      // 3) { success:true, data:[ ... ] }
      const payload = res?.data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setInventory(rows);
    } catch (e) {
      console.error("loadInventory error:", e);
      setInventory([]);
      showMsg("⚠️ Failed to load inventory");
    }
  };

  /* -------------------- cart persistence -------------------- */
  const loadCart = async () => {
    try {
      const res = await api.get(`/server.php/load-cart/${encodeURIComponent(SESSION_ID)}`);
      if (res.data?.success && Array.isArray(res.data.cart)) setCart(res.data.cart);
      else setCart([]);
    } catch (e) {
      console.warn("loadCart failed:", e?.response?.status, e?.response?.data || e?.message);
      setCart([]);
    }
  };

  const saveCart = async (cartData) => {
    try {
      await api.post("/server.php/save-cart", {
        cart_key: SESSION_ID,
        cart: cartData,
      });
    } catch (e) {
      // keep silent (POS should remain usable)
    }
  };

  useEffect(() => {
    loadInventory();
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SESSION_ID]);

  /* -------------------- search suggestions -------------------- */
  const suggestions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];

    return inventory
      .filter((i) => {
        const name = (i.itemname || i.item_name || "").toLowerCase();
        const sku = (i.sku || "").toLowerCase();
        return name.includes(q) || sku.includes(q);
      })
      .slice(0, 10);
  }, [searchTerm, inventory]);

  /* -------------------- qty modal -------------------- */
  const openQtyBox = (item) => {
    setQtyItem(item);
    setQtyInput("1");
    setQtyError("");
    setQtyBoxOpen(true);
    setTimeout(() => document.getElementById("qty-input")?.focus(), 0);
  };

  const cancelQtyBox = () => {
    setQtyBoxOpen(false);
    setQtyItem(null);
    setQtyInput("1");
    setQtyError("");
  };

  const confirmAddQty = async () => {
    const qty = Number(qtyInput);
    if (!Number.isFinite(qty) || qty <= 0) {
      setQtyError("Enter a valid quantity");
      return;
    }

    const stock = Number(qtyItem?.quantity ?? qtyItem?.qty_on_hand ?? 0);

    const inCart = cart.find((c) => Number(c.id) === Number(qtyItem.id));
    const current = Number(inCart?.quantity || 0);

    if (current + qty > stock) {
      setQtyError(`Stock limit: ${stock}`);
      return;
    }

    const price = Number(qtyItem?.sellingprice ?? qtyItem?.sell_price ?? 0);
    const name = qtyItem?.itemname ?? qtyItem?.item_name ?? "";
    const sku = qtyItem?.sku ?? "";

    const newCart = inCart
      ? cart.map((c) =>
          Number(c.id) === Number(qtyItem.id)
            ? { ...c, quantity: Number(c.quantity) + qty }
            : c
        )
      : [
          ...cart,
          {
            id: qtyItem.id,              // inventory id (UI)
            inventory_id: qtyItem.id,    // explicit DB name (compat)
            item_name: name,
            sku,
            sellingprice: price,
            quantity: qty,
          },
        ];

    setCart(newCart);
    await saveCart(newCart);
    setSearchTerm("");
    cancelQtyBox();
  };

  const removeItem = async (id) => {
    const next = cart.filter((i) => Number(i.id) !== Number(id));
    setCart(next);
    await saveCart(next);
  };

  /* -------------------- totals -------------------- */
  const total = useMemo(
    () => cart.reduce((s, i) => s + Number(i.quantity) * Number(i.sellingprice), 0),
    [cart]
  );

  const paidNum = Number(paid || 0);
  const diff = paidNum - total;
  const dueOrChangeLabel = diff < 0 ? "Amount Due" : "Change";
  const dueOrChangeValue = Math.abs(diff);

  /* -------------------- client-side receipt print -------------------- */
  const printReceiptClient = (saleId) => {
    const money = (n) => Number(n || 0).toFixed(2);

    const lines = cart.map((it) => ({
      name: it.item_name,
      qty: Number(it.quantity || 0),
      price: Number(it.sellingprice || 0),
      subtotal: Number(it.quantity || 0) * Number(it.sellingprice || 0),
    }));

    const html = `
      <div style="font-family:Arial,sans-serif; max-width:360px; margin:0 auto;">
        <div style="text-align:center; font-weight:900; font-size:16px;">Kevsoft POS</div>
        <div style="text-align:center; color:#666; font-size:12px;">Store: ${storeId}</div>
        <div style="text-align:center; color:#666; font-size:12px;">Cashier: ${escapeHtml(
          username
        )}</div>
        <div style="text-align:center; color:#666; font-size:12px;">Sale #: ${saleId}</div>
        <div style="border-top:1px dashed #333; margin:10px 0;"></div>

        <table style="width:100%; border-collapse:collapse;">
          <thead>
            <tr>
              <th style="text-align:left; font-size:12px; padding:4px 0;">Item</th>
              <th style="text-align:right; font-size:12px; padding:4px 0;">Qty</th>
              <th style="text-align:right; font-size:12px; padding:4px 0;">Price</th>
              <th style="text-align:right; font-size:12px; padding:4px 0;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${lines
              .map(
                (ln) => `
                <tr>
                  <td style="font-size:12px; padding:3px 0;">${escapeHtml(ln.name)}</td>
                  <td style="text-align:right; font-size:12px; padding:3px 0;">${ln.qty}</td>
                  <td style="text-align:right; font-size:12px; padding:3px 0;">${money(ln.price)}</td>
                  <td style="text-align:right; font-size:12px; padding:3px 0;">${money(
                    ln.subtotal
                  )}</td>
                </tr>
              `
              )
              .join("")}
          </tbody>
        </table>

        <div style="border-top:1px dashed #333; margin:10px 0;"></div>
        <div style="display:flex; justify-content:space-between; font-weight:900;">
          <div>Grand Total</div>
          <div>${money(total)}</div>
        </div>
        <div style="display:flex; justify-content:space-between;">
          <div>Paid</div>
          <div>${money(paidNum)}</div>
        </div>
        <div style="display:flex; justify-content:space-between;">
          <div>${diff < 0 ? "Balance" : "Change"}</div>
          <div>${money(diff < 0 ? Math.abs(diff) : diff)}</div>
        </div>

        <div style="text-align:center; color:#666; font-size:12px; margin-top:10px;">Thank you!</div>
      </div>
      <script>
        window.onload = () => window.print();
      </script>
    `;

    const w = window.open("", "_blank", "width=420,height=720");
    if (!w) {
      showMsg("Popup blocked. Allow popups to print.");
      return;
    }
    w.document.open();
    w.document.write(
      `<html><head><title>Receipt #${saleId}</title></head><body>${html}</body></html>`
    );
    w.document.close();
  };

  /* -------------------- checkout (SERVER: /sales) -------------------- */
  const handlePrint = async () => {
    if (!cart.length) {
      showMsg("⚠️ Cart is empty");
      return;
    }
    if (paidNum < total) {
      showMsg("⚠️ Payment is insufficient");
      return;
    }

    // Send both id + inventory_id (server accepts either)
    const payload = {
      cashier: username,
      customer: "",
      paid: paidNum,
      payment_method: "cash",
      printed: 1,
      items: cart.map((c) => ({
        id: Number(c.id),
        inventory_id: Number(c.inventory_id ?? c.id),
        qty: Number(c.quantity),
        unit_price: Number(c.sellingprice),
        sku: c.sku || "",
      })),
    };

    try {
      setBusy(true);

      const res = await api.post("/server.php/sales", payload);

      if (!res.data?.success) {
        showMsg(res.data?.message || "Sale failed");
        return;
      }

      const saleId = res.data.sale_id;

      // Print client-side
      printReceiptClient(saleId);

      // clear cart + refresh inventory
      setCart([]);
      setPaid("");
      await saveCart([]);
      await loadInventory();

      showMsg(`✅ Sale saved (ID: ${saleId})`);
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.log("SALE ERROR DATA:", status, data);

      const serverMsg =
        (typeof data === "string" ? data : (data?.error || data?.message)) || e?.message;

      showMsg(`❌ Sale/print failed (${status || "?"}): ${serverMsg || "Server error"}`);
    } finally {
      setBusy(false);
    }
  };

  /* -------------------- keyboard helpers -------------------- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        document.getElementById("sale-search")?.focus();
      }
      if (e.key === "F9") {
        e.preventDefault();
        handlePrint();
      }
      if (e.key === "Escape" && qtyBoxOpen) cancelQtyBox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyBoxOpen, cart, paid, total]);

  return (
    <div className="erp-wrap">
      <div className="erp-ribbon">
        <div className="erp-ribbon-left">
          <button className="rbtn" onClick={onBack} title="Back">
            ← Back
          </button>
          <button
            className="rbtn"
            onClick={() => document.getElementById("sale-search")?.focus()}
            title="F2"
            type="button"
          >
            Search (F2)
          </button>
          <button
            className="rbtn primary"
            onClick={handlePrint}
            disabled={busy}
            title="F9"
            type="button"
          >
            Print (F9)
          </button>
        </div>

        <div className="erp-ribbon-title">
          <span className="pill">New Sale</span>
        </div>
      </div>

      <div className="erp-head">
        <div className="field">
          <label>API</label>
          <input value={API_BASE} readOnly />
        </div>
        <div className="field">
          <label>Cashier</label>
          <input value={username} readOnly />
        </div>
        <div className="field">
          <label>Store</label>
          <input value={String(storeId)} readOnly />
        </div>
        <div className="field">
          <label>Session</label>
          <input value={SESSION_ID} readOnly />
        </div>
      </div>

      {msg && <div className="erp-status">{msg}</div>}

      <div className="erp-body">
        <div className="erp-left">
          <div className="erp-search">
            <input
              id="sale-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search item name or SKU (F2)..."
              disabled={busy}
              autoComplete="off"
            />
            <div className="hint">Tip: Select item → enter quantity → Add</div>

            {searchTerm && suggestions.length > 0 && (
              <div className="erp-suggest">
                {suggestions.map((i) => {
                  const stock = Number(i.quantity ?? i.qty_on_hand ?? 0);
                  const disabled = stock <= 0;

                  const name = i.itemname ?? i.item_name ?? "";
                  const price = Number(i.sellingprice ?? i.sell_price ?? 0);

                  return (
                    <button
                      key={i.id}
                      className={`srow ${disabled ? "disabled" : ""}`}
                      onClick={() => !disabled && openQtyBox(i)}
                      type="button"
                    >
                      <span className="sname">{name}</span>
                      <span className="smeta">
                        <span className="tag">Stock: {stock}</span>
                        <span className="tag">Price: {price.toFixed(2)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {qtyBoxOpen && qtyItem && (
            <div className="erp-modal" role="dialog" aria-modal="true" aria-label="Add item">
              <div className="erp-modal-card">
                <div className="mhead">
                  <div className="mtitle">Add Item</div>
                  <button className="x" onClick={cancelQtyBox} type="button" aria-label="Close">
                    ✕
                  </button>
                </div>

                <div className="mbody">
                  <div className="mline">
                    <div className="mlabel">Item</div>
                    <div className="mvalue">{qtyItem.itemname ?? qtyItem.item_name ?? ""}</div>
                  </div>

                  <div className="mgrid">
                    <div className="field">
                      <label>Price</label>
                      <input
                        value={Number(qtyItem.sellingprice ?? qtyItem.sell_price ?? 0).toFixed(2)}
                        readOnly
                      />
                    </div>
                    <div className="field">
                      <label>Stock</label>
                      <input value={String(qtyItem.quantity ?? qtyItem.qty_on_hand ?? 0)} readOnly />
                    </div>
                    <div className="field">
                      <label>Quantity</label>
                      <input
                        id="qty-input"
                        type="number"
                        min="1"
                        value={qtyInput}
                        onChange={(e) => setQtyInput(e.target.value)}
                      />
                    </div>
                  </div>

                  {qtyError && <div className="err">{qtyError}</div>}

                  <div className="mactions">
                    <button className="btn primary" onClick={confirmAddQty} type="button">
                      Add
                    </button>
                    <button className="btn" onClick={cancelQtyBox} type="button">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="erp-grid">
            <div className="grid-head">
              <div className="grid-title">Particulars</div>
              <div className="grid-sub">Items: {cart.length}</div>
            </div>

            <div className="grid-table-wrap">
              <table className="grid-table">
                <thead>
                  <tr>
                    <th style={{ width: "46%" }}>Name</th>
                    <th className="r" style={{ width: "12%" }}>
                      Qty
                    </th>
                    <th className="r" style={{ width: "16%" }}>
                      Price
                    </th>
                    <th className="r" style={{ width: "18%" }}>
                      Amount
                    </th>
                    <th className="r" style={{ width: "8%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((i) => (
                    <tr key={i.id}>
                      <td className="name">{i.item_name}</td>
                      <td className="r">{i.quantity}</td>
                      <td className="r">{Number(i.sellingprice).toFixed(2)}</td>
                      <td className="r">
                        {(Number(i.quantity) * Number(i.sellingprice)).toFixed(2)}
                      </td>
                      <td className="r">
                        <button
                          className="icon"
                          onClick={() => removeItem(i.id)}
                          type="button"
                          title="Remove"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}

                  {cart.length === 0 && (
                    <tr>
                      <td className="empty" colSpan={5}>
                        No items yet. Search to add items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid-foot">
              <div className="foot-note">Shortcuts: F2 Search · F9 Print · Esc Close</div>
            </div>
          </div>
        </div>

        <div className="erp-right">
          <div className="pay-card">
            <div className="pay-head">Payment Details</div>

            <div className="pay-row">
              <span>Total Amount</span>
              <b>{total.toFixed(2)}</b>
            </div>

            <div className="pay-field">
              <label>Cash Amount (Paid)</label>
              <input
                value={paid}
                onChange={(e) => setPaid(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                disabled={busy}
              />
            </div>

            <div className={`pay-row ${diff < 0 ? "bad" : "good"}`}>
              <span>{dueOrChangeLabel}</span>
              <b>{dueOrChangeValue.toFixed(2)}</b>
            </div>

            <div className="pay-actions">
              <button className="btn primary" onClick={handlePrint} disabled={busy} type="button">
                🖨 Print
              </button>
              <button className="btn" onClick={onBack} disabled={busy} type="button">
                Back
              </button>
            </div>

            <div className="pay-help">
              <div>
                <b>Note</b>: “Amount Due” appears if Paid is less than Total.
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{css}</style>
    </div>
  );
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/* -------------------- ERP-inspired CSS -------------------- */
const css = `
.erp-wrap{font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;padding:12px}
.erp-ribbon{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border:1px solid #d7dde6;border-radius:12px;background:#fff;box-shadow:0 6px 18px rgba(0,0,0,.06)}
.erp-ribbon-left{display:flex;gap:8px;align-items:center}
.erp-ribbon-title .pill{display:inline-block;padding:6px 10px;border-radius:999px;border:1px solid #d7dde6;background:#f7f9fc;font-weight:800}
.rbtn{border:1px solid #cfd6df;background:#fff;border-radius:10px;padding:8px 10px;cursor:pointer}
.rbtn:hover{background:#f6f8fb}
.rbtn.primary{background:#111827;color:#fff;border-color:#111827}
.rbtn.primary:hover{background:#0b1220}
.erp-head{margin-top:10px;display:grid;grid-template-columns:repeat(4,minmax(160px,1fr));gap:10px}
.field label{display:block;font-size:12px;color:#6b7280;margin:0 0 4px}
.field input{width:100%;border:1px solid #cfd6df;border-radius:10px;padding:8px 10px;background:#fff}
.erp-status{margin-top:10px;padding:10px 12px;border-radius:12px;border:1px solid #d7dde6;background:#fff}
.erp-body{margin-top:12px;display:grid;grid-template-columns:1.6fr .7fr;gap:12px;align-items:start}
.erp-left{display:flex;flex-direction:column;gap:12px}
.erp-right{position:sticky;top:10px}
.erp-search{border:1px solid #d7dde6;border-radius:12px;background:#fff;padding:12px;box-shadow:0 6px 18px rgba(0,0,0,.06);position:relative}
.erp-search input{width:100%;border:1px solid #cfd6df;border-radius:10px;padding:10px 12px}
.hint{margin-top:6px;color:#6b7280;font-size:12px}
.erp-suggest{margin-top:10px;border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;max-height:320px;overflow:auto}
.srow{width:100%;text-align:left;border:0;border-bottom:1px solid #eef2f7;background:#fff;padding:10px 12px;cursor:pointer;display:flex;justify-content:space-between;gap:12px}
.srow:hover{background:#f8fafc}
.srow.disabled{opacity:.55;cursor:not-allowed}
.sname{font-weight:800}
.smeta{display:flex;gap:8px;align-items:center}
.tag{font-size:12px;color:#374151;border:1px solid #e5e7eb;background:#f9fafb;padding:3px 8px;border-radius:999px}
.erp-grid{border:1px solid #d7dde6;border-radius:12px;background:#fff;box-shadow:0 6px 18px rgba(0,0,0,.06)}
.grid-head{display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border-bottom:1px solid #eef2f7}
.grid-title{font-weight:900}
.grid-sub{color:#6b7280;font-size:12px}
.grid-table-wrap{overflow:auto}
.grid-table{width:100%;border-collapse:collapse}
.grid-table th,.grid-table td{padding:10px 12px;border-bottom:1px solid #eef2f7;font-size:14px}
.grid-table th{background:#f9fafb;font-size:12px;color:#374151;text-transform:uppercase;letter-spacing:.04em}
.grid-table td.name{font-weight:700}
.grid-table td.empty{color:#6b7280;text-align:center;padding:18px}
.grid-table .r{text-align:right}
.icon{border:1px solid #e5e7eb;background:#fff;border-radius:10px;padding:6px 8px;cursor:pointer}
.icon:hover{background:#f6f8fb}
.grid-foot{padding:10px 12px;color:#6b7280;font-size:12px}
.pay-card{border:1px solid #d7dde6;border-radius:12px;background:#fff;box-shadow:0 6px 18px rgba(0,0,0,.06);padding:12px}
.pay-head{font-weight:900;margin-bottom:10px}
.pay-row{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px dashed #e5e7eb}
.pay-row.good b{color:#065f46}
.pay-row.bad b{color:#b91c1c}
.pay-field{margin-top:10px}
.pay-field input{width:100%;border:1px solid #cfd6df;border-radius:10px;padding:10px 12px}
.pay-actions{display:flex;gap:10px;margin-top:12px}
.btn{border:1px solid #cfd6df;background:#fff;border-radius:10px;padding:10px 12px;cursor:pointer;flex:1}
.btn.primary{background:#111827;color:#fff;border-color:#111827}
.btn.primary:hover{background:#0b1220}
.pay-help{margin-top:10px;color:#6b7280;font-size:12px}
.erp-modal{position:fixed;inset:0;background:rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;z-index:2000;padding:12px}
.erp-modal-card{width:min(560px,96vw);background:#fff;border:1px solid #cfd6df;border-radius:14px;box-shadow:0 22px 60px rgba(0,0,0,.25)}
.mhead{display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border-bottom:1px solid #eef2f7}
.mtitle{font-weight:900}
.x{border:1px solid #e5e7eb;background:#fff;border-radius:10px;padding:6px 10px;cursor:pointer}
.mbody{padding:12px}
.mline{display:flex;justify-content:space-between;gap:10px;margin-bottom:10px}
.mlabel{color:#6b7280}
.mvalue{font-weight:800}
.mgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.err{margin-top:8px;color:#b91c1c;font-weight:700}
.mactions{display:flex;gap:10px;margin-top:12px;justify-content:flex-end}
@media (max-width: 980px){
  .erp-body{grid-template-columns:1fr}
  .erp-right{position:relative;top:auto}
  .erp-head{grid-template-columns:1fr 1fr}
}
`;